import React, { useContext, useEffect, useState } from "react";
import Breadcrumb from "../../CommonComponents/BreadCrumb/BreadCrumb";
import Button from "../../Elements/Button";
import Input from "../../Elements/Input";
import Select from "../../Elements/Select";
import UserInfoContext from "../../../utils/userInfoContext";
import apiGETRequest from "../../../utils/apiRequest";
import { networkService } from "../../config/axios";
import { getAvatarData } from "../../../utils/avatarUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "./myProfilePage.css";

const EMPTY_PASSWORD_FORM = {
  current_password: "",
  password: "",
  password_confirmation: "",
};

// Mirror the server-side rules enforced on sign up and on password reset
// (User#password_complexity + Devise's minimum length).
const getPasswordRules = (value) => ({
  minLength: value.length >= 6,
  uppercase: /[A-Z]/.test(value),
  lowercase: /[a-z]/.test(value),
  digit: /\d/.test(value),
  special: /[^A-Za-z0-9]/.test(value),
});

const PASSWORD_RULE_LABELS = {
  minLength: "Minimum 6 characters",
  uppercase: "At least one uppercase letter",
  lowercase: "At least one lowercase letter",
  digit: "At least one digit",
  special: "At least one special character",
};

const GEO_PROVIDER_INFO = {
  iplocate: { signupUrl: "https://www.iplocate.io/", tagline: "1000 free IP look-ups daily" },
  maxmind: { signupUrl: "https://www.maxmind.com/en/home", tagline: "Fraud detection tool" },
  ip2location: { signupUrl: "https://www.ip2location.io/pricing", tagline: "Proxy Threat Intelligence" },
};

// A native <select> shows the selected option's own label even when closed, so the
// tagline appended here is visible both in the open list and as the collapsed value --
// there's no way to show a shorter closed-state label than the option itself provides.
const GEO_PROVIDER_OPTIONS = [
  { value: "iplocate", title: `IPLocate.io — ${GEO_PROVIDER_INFO.iplocate.tagline}` },
  { value: "ip2location", title: `IP2Location — ${GEO_PROVIDER_INFO.ip2location.tagline}` },
  { value: "maxmind", title: `MaxMind — ${GEO_PROVIDER_INFO.maxmind.tagline}` },
];

const GEO_KEY_LABELS = {
  iplocate: "IPLocate.io API Key",
  ip2location: "IP2Location API Key",
  maxmind: "MaxMind License Key",
};

const buildFields = (profileData) => ({
  first_name: { value: profileData?.first_name || "", required: true },
  last_name: { value: profileData?.last_name || "", required: true },
  time_zone: { value: profileData?.time_zone || "", required: true },
  // Org members inherit the company's name and can't edit it (enforced server-side too)
  company_name: { value: profileData?.company_name || "", required: !profileData?.company_name_locked },
  country: { value: profileData?.country || profileData?.detected_country || "", required: true },
  company_website: { value: profileData?.company_website || "", required: !profileData?.company_website_locked },
  privacy_link: { value: profileData?.privacy_link || "", required: !profileData?.privacy_link_locked },
  direct_chat_link: { value: profileData?.direct_chat_link || "", required: false },
  // Optional -- location tracking works via a house fallback account when no provider
  // is configured (see Geo::Resolver), so nobody is forced to pick one.
  geo_provider: { value: profileData?.geo_provider || "", required: false },
  // The key/account id are never sent back down once saved (see _profile.json.jbuilder),
  // so geo_api_key always starts blank; geo_account_id isn't a secret so it's safe to
  // round-trip. "required" here is a static default only -- both fields' real
  // required-ness depends on whether the *currently selected* provider matches what's
  // already saved, which can change as the user edits the form, so it's recomputed live
  // in validateGeoFields at save time rather than trusted from this initial value.
  geo_api_key: { value: "", required: !profileData?.geo_key_present },
  geo_account_id: { value: profileData?.geo_account_id || "", required: false },
  // Never sent back once saved (see _profile.json.jbuilder); blank on save means "keep current key".
  unsplash_access_key: { value: "", required: false },
});

// Mirrors handleSave's generic error computation, but with the provider-dependent
// logic the generic per-field "required" flag can't express on its own: the key/account
// id are only truly required if the user is on a *different* provider than what's saved,
// or nothing was ever saved at all.
const validateGeoFields = (fields, profileData) => {
  const provider = fields.geo_provider.value;
  const errors = {};
  // Choosing a provider is optional -- nothing left to validate once none is selected.
  if (!provider) return errors;

  const providerUnchanged = provider === profileData?.geo_provider;
  const keyAlreadySaved = providerUnchanged && profileData?.geo_key_present;
  if (!keyAlreadySaved && !fields.geo_api_key.value.trim()) errors.geo_api_key = true;

  if (provider === "maxmind") {
    const accountIdAlreadySaved = providerUnchanged && !!profileData?.geo_account_id;
    if (!accountIdAlreadySaved && !fields.geo_account_id.value.trim()) errors.geo_account_id = true;
  }

  return errors;
};

const MyProfilePage = () => {
  const { profileData, userInfoData, setUserProfileData, setShowNotifications, refetchUserProfile } = useContext(UserInfoContext);
  const [fields, setFields] = useState(buildFields(profileData));
  const [timeZoneData, setTimeZoneData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadedProfileId, setLoadedProfileId] = useState(null);

  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordServerErrors, setPasswordServerErrors] = useState([]);

  const passwordRules = getPasswordRules(passwordForm.password);
  const passwordRulesMet = Object.values(passwordRules).every(Boolean);
  const passwordsMatch =
    passwordForm.password.length > 0 &&
    passwordForm.password === passwordForm.password_confirmation;
  const canSubmitPassword =
    !!passwordForm.current_password &&
    passwordRulesMet &&
    passwordsMatch &&
    !passwordSaving;

  useEffect(() => {
    apiGETRequest("time-zones").then((data) => setTimeZoneData(data || {}));
  }, []);

  // Deep-link support for the "sign up" dialog's button (navigate("/profile#geo-provider-section"))
  // and the editor's Unsplash tab ("/profile#unsplash-section").
  // Deferred a tick so this runs after the initial layout settles, not before the target exists.
  useEffect(() => {
    const target = { "#geo-provider-section": "geo-provider-section", "#unsplash-section": "unsplash-section" }[window.location.hash];
    if (!target) return;
    const timer = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Re-derive fields whenever the loaded profile changes OR its key-present flag flips
    // (e.g. right after the first save), so "required" and the placeholder stay accurate.
    const loadedKey = `${profileData?.id}-${profileData?.geo_provider}-${profileData?.geo_key_present}-${profileData?.unsplash_key_present}`;
    if (profileData && loadedProfileId !== loadedKey) {
      setFields(buildFields(profileData));
      setLoadedProfileId(loadedKey);
    }
  }, [profileData, loadedProfileId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({
      ...prev,
      // Editing a field clears any server-side rejection message it was showing --
      // otherwise a stale "key rejected by provider" error would linger under a field
      // the user is actively retyping.
      [name]: { ...prev[name], value, error: prev[name].required && value.trim() === "", errorText: undefined },
    }));
  };

  // Switching providers means whatever key/account id is currently typed belongs to the
  // OLD provider -- clear both rather than leave a stale value that looks like it
  // belongs to the newly selected one (matches profiles_controller#update's same rule
  // for what gets persisted).
  const handleGeoProviderChange = (e) => {
    const { value } = e.target;
    setFields((prev) => ({
      ...prev,
      geo_provider: { ...prev.geo_provider, value, error: false },
      geo_api_key: { ...prev.geo_api_key, value: "", error: false },
      geo_account_id: { ...prev.geo_account_id, value: "", error: false },
    }));
  };

  const handleSave = async () => {
    const withErrors = Object.entries(fields).reduce((acc, [name, field]) => {
      acc[name] = { ...field, error: field.required && !field.value?.trim() };
      return acc;
    }, {});

    const geoErrors = validateGeoFields(fields, profileData);
    Object.keys(geoErrors).forEach((name) => {
      withErrors[name] = { ...withErrors[name], error: true };
    });

    if (Object.values(withErrors).some((field) => field.error)) {
      setFields(withErrors);
      return;
    }

    setSaving(true);
    const dataToUpload = Object.entries(fields).reduce((acc, [name, field]) => {
      acc[name] = field.value;
      return acc;
    }, {});
    if (profileData?.company_name_locked) delete dataToUpload.company_name;
    if (profileData?.privacy_link_locked) delete dataToUpload.privacy_link;
    if (profileData?.company_website_locked) delete dataToUpload.company_website;

    try {
      const { data, status } = await networkService(
        `profiles/${userInfoData.id}.json`,
        "PATCH",
        { profile: { ...dataToUpload }, user_id: userInfoData.id }
      );
      if (status === 200 && data) {
        setUserProfileData(data);
        setShowNotifications({ showToast: true, toastType: "success", toastMessage: "Profile details saved" });
        refetchUserProfile?.();
      }
    } catch (err) {
      // The server actually calls the chosen provider's API with the submitted key/account
      // id before saving (see ProfilesController#update) -- a rejection comes back as a 422
      // with { errors: { geo_api_key: ["..."] } }, same shape as any other model validation.
      const apiErrors = err?.response?.data?.errors || {};
      const geoFieldNames = ["geo_api_key", "geo_account_id", "unsplash_access_key", "company_website", "privacy_link"];
      if (geoFieldNames.some((name) => apiErrors[name]?.length)) {
        setFields((prev) => {
          const updated = { ...prev };
          geoFieldNames.forEach((name) => {
            if (apiErrors[name]?.length) {
              updated[name] = { ...updated[name], error: true, errorText: apiErrors[name].join(" ") };
            }
          });
          return updated;
        });
      }
      const message = Object.values(apiErrors).flat()[0] || "Could not save profile details. Please try again.";
      setShowNotifications({ showToast: true, toastType: "danger", toastMessage: message });
    } finally {
      setSaving(false);
    }
  };

  const [removingUnsplashKey, setRemovingUnsplashKey] = useState(false);
  const handleRemoveUnsplashKey = async () => {
    if (!window.confirm("Remove your Unsplash Access Key? The Unsplash tab in the editor will stop loading photos until a new key is added.")) return;
    setRemovingUnsplashKey(true);
    try {
      const { data, status } = await networkService(
        `profiles/${userInfoData.id}.json`,
        "PATCH",
        { profile: { remove_unsplash_access_key: true }, user_id: userInfoData.id }
      );
      if (status === 200 && data) {
        setUserProfileData(data);
        setShowNotifications({ showToast: true, toastType: "success", toastMessage: "Unsplash Access Key removed" });
        refetchUserProfile?.();
      }
    } catch (err) {
      setShowNotifications({ showToast: true, toastType: "danger", toastMessage: "Could not remove the Unsplash Access Key. Please try again." });
    } finally {
      setRemovingUnsplashKey(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPasswordTouched(true);
    setPasswordServerErrors([]);
  };

  const handlePasswordUpdate = async () => {
    if (!canSubmitPassword) {
      setPasswordTouched(true);
      return;
    }

    setPasswordSaving(true);
    setPasswordServerErrors([]);

    try {
      const { status } = await networkService("account/password", "PATCH", {
        user: {
          current_password: passwordForm.current_password,
          password: passwordForm.password,
          password_confirmation: passwordForm.password_confirmation,
        },
      });

      if (status === 200) {
        setPasswordForm(EMPTY_PASSWORD_FORM);
        setPasswordTouched(false);
        setShowNotifications({
          showToast: true,
          toastType: "success",
          toastMessage: "Password updated",
        });
      }
    } catch (err) {
      const data = err?.response?.data || {};
      const fieldErrors = data.field_errors || {};
      let messages = [];
      if (fieldErrors.current_password?.length) {
        messages.push("Your current password is incorrect.");
      }
      (data.errors || []).forEach((msg) => {
        if (!/current password/i.test(msg) && msg !== "is invalid") messages.push(msg);
      });
      messages = [...new Set(messages)];
      if (!messages.length) {
        messages = [data.warning || "Could not update password. Please try again."];
      }
      setPasswordServerErrors(messages);
    } finally {
      setPasswordSaving(false);
    }
  };

  const avatarData = getAvatarData(profileData?.first_name || userInfoData?.name || userInfoData?.email || "");
  const displayName = [fields.first_name.value, fields.last_name.value].filter(Boolean).join(" ") || userInfoData?.email;

  // Whether the currently selected provider/key/account id are exactly what's already
  // saved (nothing left to do), vs. the user switching providers or typing a replacement
  // key/id -- drives the Sign Up button's "already configured" look.
  const geoProviderUnchanged = fields.geo_provider.value === profileData?.geo_provider;
  const geoKeyAlreadySaved = geoProviderUnchanged && profileData?.geo_key_present;
  const geoAccountIdAlreadySaved = geoProviderUnchanged && !!profileData?.geo_account_id;
  const isGeoConfigured =
    !!fields.geo_provider.value &&
    geoKeyAlreadySaved &&
    !fields.geo_api_key.value.trim() &&
    (fields.geo_provider.value !== "maxmind" ||
      (geoAccountIdAlreadySaved && fields.geo_account_id.value === profileData?.geo_account_id));

  // Nothing pending to save (every field still matches what's persisted) -- keeps Save
  // Changes from staying green/clickable once a save has gone through, or on first load.
  const savedFields = buildFields(profileData);
  const isProfileDirty = Object.keys(fields).some((name) =>
    ["geo_api_key", "unsplash_access_key"].includes(name) ? fields[name].value.trim() !== "" : fields[name].value !== savedFields[name].value
  );

  return (
    <div className="my-profile-page">
      <Breadcrumb string="My Profile" />

      <div className="my-profile-card">
        <button type="button" className="my-profile-close" aria-label="Close" title="Close" onClick={() => { window.location.href = "/kdfs"; }}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className="my-profile-identity">
          <div className="my-profile-avatar" style={{ backgroundColor: avatarData.color }}>
            {avatarData.initial}
          </div>
          <div>
            <div className="my-profile-name">{displayName}</div>
            <div className="my-profile-email">{userInfoData?.email}</div>
          </div>
        </div>

        <form name="myProfileForm" onSubmit={(e) => e.preventDefault()}>
          <div className="my-profile-section">
            <h3 className="my-profile-section-title">Personal Information</h3>
            <div className="my-profile-grid">
              <Input
                classList=""
                type="text"
                name="first_name"
                label="First Name"
                required
                placeholder="Enter First Name"
                value={fields.first_name.value}
                errorMessageStatus={fields.first_name.error}
                onChange={handleChange}
              />
              <Input
                classList=""
                type="text"
                name="last_name"
                label="Last Name"
                required
                placeholder="Enter Last Name"
                value={fields.last_name.value}
                errorMessageStatus={fields.last_name.error}
                onChange={handleChange}
              />
              <div className="input-groups">
                <div className="form-input">
                  <label className="control-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={userInfoData?.email || ""}
                    readOnly
                    tabIndex={-1}
                    onKeyDown={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                  />
                </div>
              </div>
              <div className="input-groups">
                <div className="form-input">
                  <label className="control-label">*Time Zone</label>
                  <Select
                    classList=""
                    name="time_zone"
                    required
                    optionList={Object.keys(timeZoneData).map((key) => ({
                      value: timeZoneData[key].id,
                      title: timeZoneData[key].name,
                    }))}
                    value={fields.time_zone.value}
                    errorMessageStatus={fields.time_zone.error}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="input-groups">
                <div className="form-input">
                  <label className="control-label">* Country</label>
                  <Select
                    classList=""
                    name="country"
                    required
                    placeholder="Select Country"
                    optionList={(profileData?.country_options || []).map((name) => ({ value: name, title: name }))}
                    value={fields.country.value}
                    errorMessageStatus={fields.country.error}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="my-profile-divider" />

          <div className="my-profile-section">
            <h3 className="my-profile-section-title">{profileData?.individual ? "Business Information" : "Company Information"}</h3>
            {profileData?.company_name_locked && (
              <p className="my-profile-section-hint">Your company name is managed by your company account.</p>
            )}
            <div className="my-profile-grid">
              <Input
                classList=""
                type="text"
                name="company_name"
                label={profileData?.individual ? "Business Name" : "Company Name"}
                required={!profileData?.company_name_locked}
                readOnly={!!profileData?.company_name_locked}
                title={profileData?.company_name_locked ? "Set by your company account" : undefined}
                placeholder={profileData?.individual ? "Enter Business Name" : "Enter Company Name"}
                value={fields.company_name.value}
                errorMessageStatus={fields.company_name.error}
                onChange={handleChange}
              />
              <Input
                classList="my-profile-grid-full"
                type="text"
                name="company_website"
                label={profileData?.individual ? "Your Website" : "Company Website"}
                required={!profileData?.company_website_locked}
                readOnly={!!profileData?.company_website_locked}
                title={profileData?.company_website_locked ? "Set by your company account" : undefined}
                placeholder="https://yourcompany.com"
                value={fields.company_website.value}
                errorMessageStatus={fields.company_website.error}
                errorMessage={fields.company_website.errorText && <span className="text-danger">{fields.company_website.errorText}</span>}
                onChange={handleChange}
              />
              <Input
                classList="my-profile-grid-full"
                type="text"
                name="privacy_link"
                label={profileData?.individual ? "Your Privacy Notice URL" : "Privacy Notice URL"}
                required={!profileData?.privacy_link_locked}
                readOnly={!!profileData?.privacy_link_locked}
                title={profileData?.privacy_link_locked ? "Set by your company account" : undefined}
                placeholder="https://yourcompany.com/privacy"
                value={fields.privacy_link.value}
                errorMessageStatus={fields.privacy_link.error}
                errorMessage={fields.privacy_link.errorText && <span className="text-danger">{fields.privacy_link.errorText}</span>}
                onChange={handleChange}
              >
                <p className="my-profile-field-hint">
                  {profileData?.privacy_link_locked
                    ? "Your company's Privacy Notice is managed by your company admin."
                    : "Shown to people who open your LaserLINX. Must be on the same domain as your website, usually linked as \"Privacy\" in its footer."}
                </p>
              </Input>
              <Input
                classList="my-profile-grid-full"
                type="text"
                name="direct_chat_link"
                label="Direct Chat Link (Tawk.to)"
                placeholder="Enter Direct Chat Link"
                value={fields.direct_chat_link.value}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="my-profile-divider" />

          <div className="my-profile-section" id="geo-provider-section">
            <h3 className="my-profile-section-title">Integrations</h3>
            <p className="my-profile-section-hint">
              Used to detect a recipient's approximate location and VPN/proxy status when they view a KDF.
              Includes 50 free recipient location look-ups. To keep tracking after that, pick a provider below and add your own account.
            </p>
            <div className="my-profile-grid">
              <div className="input-groups my-profile-grid-full">
                <div className="form-input">
                  <label className="control-label">Geolocation Provider (optional)</label>
                  <div className="geo-provider-row">
                    <div className="geo-provider-select-col">
                      <Select
                        classList="geo-provider-select"
                        name="geo_provider"
                        optionList={GEO_PROVIDER_OPTIONS}
                        value={fields.geo_provider.value}
                        errorMessageStatus={fields.geo_provider.error}
                        onChange={handleGeoProviderChange}
                      />
                    </div>
                    {fields.geo_provider.value && (
                      <a
                        href={GEO_PROVIDER_INFO[fields.geo_provider.value].signupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`geo-provider-signup-btn${isGeoConfigured ? " geo-provider-signup-btn--saved" : ""}`}
                      >
                        Sign Up
                      </a>
                    )}
                  </div>
                </div>
              </div>
              {fields.geo_provider.value && (
                <>
                  <Input
                    classList="my-profile-grid-full small-placeholder"
                    type="secret"
                    name="geo_api_key"
                    label={GEO_KEY_LABELS[fields.geo_provider.value]}
                    autoComplete="off"
                    required={!geoKeyAlreadySaved}
                    placeholder={
                      geoKeyAlreadySaved
                        ? "Key saved — enter a new key to replace it"
                        : `Your ${GEO_KEY_LABELS[fields.geo_provider.value]} has been implemented, if you'd like to replace it, paste the new one here.`
                    }
                    value={fields.geo_api_key.value}
                    errorMessageStatus={fields.geo_api_key.error || (!geoKeyAlreadySaved && !fields.geo_api_key.value.trim())}
                    errorMessage={fields.geo_api_key.errorText && <span className="text-danger">{fields.geo_api_key.errorText}</span>}
                    onChange={handleChange}
                  />
                  {fields.geo_provider.value === "maxmind" && (
                    <Input
                      classList="my-profile-grid-full"
                      type="text"
                      name="geo_account_id"
                      label="MaxMind Account ID"
                      required={!geoAccountIdAlreadySaved}
                      placeholder="Enter your MaxMind Account ID"
                      value={fields.geo_account_id.value}
                      errorMessageStatus={
                        fields.geo_account_id.error || (!geoAccountIdAlreadySaved && !fields.geo_account_id.value.trim())
                      }
                      errorMessage={
                        fields.geo_account_id.errorText && <span className="text-danger">{fields.geo_account_id.errorText}</span>
                      }
                      onChange={handleChange}
                    />
                  )}
                </>
              )}
            </div>

            <div id="unsplash-section" className="my-profile-subsection">
              <h4 className="my-profile-subsection-title">Unsplash</h4>
              <p className="my-profile-section-hint">
                Used to search and add Unsplash photos in the editor.
              </p>
              <div className="my-profile-grid">
                <Input
                  classList="my-profile-grid-full small-placeholder"
                  type="secret"
                  name="unsplash_access_key"
                  label="Unsplash Access Key"
                  autoComplete="off"
                  placeholder={
                    profileData?.unsplash_key_present
                      ? "Key saved — enter a new key to replace it"
                      : "Paste your Unsplash Access Key (not the Secret Key)"
                  }
                  value={fields.unsplash_access_key.value}
                  errorMessageStatus={fields.unsplash_access_key.error}
                  errorMessage={fields.unsplash_access_key.errorText && <span className="text-danger">{fields.unsplash_access_key.errorText}</span>}
                  onChange={handleChange}
                />
                <div className="my-profile-grid-full unsplash-key-actions">
                  <a href="https://unsplash.com/oauth/applications" target="_blank" rel="noopener noreferrer">
                    Get a free Access Key from Unsplash
                  </a>
                  {profileData?.unsplash_key_present && (
                    <button
                      type="button"
                      className="unsplash-key-remove"
                      disabled={removingUnsplashKey}
                      onClick={handleRemoveUnsplashKey}
                    >
                      {removingUnsplashKey ? "Removing..." : "Remove saved key"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="my-profile-actions">
            <Button
              classList="alert"
              addonClass="me-2"
              text="Cancel"
              onClick={() => { window.location.href = "/kdfs"; }}
            />
            <Button
              classList="success"
              text={saving ? "Saving..." : "Save Changes"}
              type="submit"
              disabled={saving || !isProfileDirty}
              onClick={handleSave}
            />
          </div>
        </form>

        <div className="my-profile-divider" />

        <form name="changePasswordForm" onSubmit={(e) => e.preventDefault()}>
          <div className="my-profile-section">
            <h3 className="my-profile-section-title">Password</h3>
            <p className="my-profile-section-hint">
              Choose a strong password you don't use anywhere else.
            </p>

            <div className="my-profile-grid">
              <Input
                classList=""
                type="password"
                name="current_password"
                label="Current Password"
                required
                placeholder="Enter current password"
                autoComplete="current-password"
                value={passwordForm.current_password}
                onChange={handlePasswordChange}
              />
              <div />

              <Input
                classList=""
                type="password"
                name="password"
                label="New Password"
                required
                placeholder="Enter new password"
                autoComplete="new-password"
                value={passwordForm.password}
                onChange={handlePasswordChange}
              />

              <Input
                classList=""
                type="password"
                name="password_confirmation"
                label="Confirm New Password"
                required
                placeholder="Re-enter new password"
                autoComplete="new-password"
                value={passwordForm.password_confirmation}
                errorMessageStatus={
                  passwordForm.password_confirmation.length > 0 && !passwordsMatch
                }
                onChange={handlePasswordChange}
              />
            </div>

            {(passwordTouched || passwordForm.password.length > 0) && (
              <ul className="my-profile-pw-rules">
                {Object.entries(PASSWORD_RULE_LABELS).map(([key, label]) => (
                  <li key={key} className={passwordRules[key] ? "valid" : "invalid"}>
                    {label}
                  </li>
                ))}
              </ul>
            )}

            {passwordForm.password_confirmation.length > 0 && !passwordsMatch && (
              <p className="my-profile-pw-mismatch">
                New password and confirmation don't match.
              </p>
            )}

            {passwordServerErrors.length > 0 && (
              <ul className="my-profile-pw-server-errors">
                {passwordServerErrors.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )}

            <div className="my-profile-actions my-profile-actions--section">
              <Button
                classList="success"
                text={passwordSaving ? "Updating..." : "Update Password"}
                type="submit"
                disabled={!canSubmitPassword}
                onClick={handlePasswordUpdate}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyProfilePage;
